/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import Heading from '@atlaskit/heading';
import { FormattedMessage } from 'react-intl';
import { isMessagesKey } from '../../util/type-helpers';
import { messages } from '../i18n';
import type { CategoryGroupKey } from './categories';

const emojiCategoryTitle = css({
	boxSizing: 'border-box',
	color: token('color.text'),
	font: token('font.body'),
	paddingTop: token('space.075'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	textTransform: 'lowercase',
	// Reset heading element default styles to avoid visual changes
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:first-letter': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
	},
});

/**
 * Test id for wrapper Emoji Picker List div
 */
export const RENDER_EMOJI_PICKER_CATEGORY_HEADING_TESTID = 'render-emoji-picker-categorty-heading';

export interface Props {
	className?: string;
	id: CategoryGroupKey;
	title: string;
}

const EmojiPickerCategoryHeading = ({ id, title, className }: Props): JSX.Element => (
	<div
		id={id}
		data-category-id={id}
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		className={className}
		data-testid={RENDER_EMOJI_PICKER_CATEGORY_HEADING_TESTID}
		// A11Y-31084: With the list-markup experiment (or the existing reaction-emoji
		// flag) the picker no longer uses grid semantics, so the category heading
		// must not be a "rowheader" (which requires a row parent).
		role={
			fg('platform_a11y_fixes_reaction_emoji') ||
			expValEqualsNoExposure('platform_a11y_fixes_emoji_picker_list', 'isEnabled', true)
				? 'presentation'
				: 'rowheader'
		}
	>
		{fg('platform_emoji_a11y_category_heading') ? (
			<div css={emojiCategoryTitle}>
				<Heading size="xsmall" as="h2">
					{isMessagesKey(title) ? <FormattedMessage {...messages[title]} /> : title}
				</Heading>
			</div>
		) : (
			<div css={emojiCategoryTitle}>
				{isMessagesKey(title) ? <FormattedMessage {...messages[title]} /> : title}
			</div>
		)}
	</div>
);

export default EmojiPickerCategoryHeading;
